import React, { useEffect, useState } from "react";

/* Template Live Preview — mock storefront dari Design + DNA + Demo Content.
   Pure visual — tidak memanggil API, tidak menjalankan engine tenant. */
export const TemplateLivePreview = ({ design = {}, dna = {}, demoContent = {} }) => {
  const d = design || {};
  const primary = d.primary_color || "#0ea5e9";
  const accent = d.accent_color || "#8b5cf6";
  const bg = d.background_color || "#ffffff";
  const text = d.text_color || "#0f172a";
  const font = d.typography_body || "Inter";
  const brand = demoContent?.brand_name || d.seo_title || "Nama Brand";
  const sections = dna?.homepage_sections || [];
  const products = demoContent?.products || [];
  const banners = (demoContent?.banners || []).slice(0, 5);
  const isSlider = banners.length > 1 && dna?.banner_style !== "static";
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (!isSlider) return undefined;
    const t = setInterval(() => setSlide((s) => (s + 1) % banners.length), 3000);
    return () => clearInterval(t);
  }, [isSlider, banners.length]);
  const banner = banners[isSlider ? slide % banners.length : 0];
  const btnRadius = d.button_style === "pill" ? "9999px" : d.button_style === "sharp" ? "2px" : "8px";
  const cardRadius = d.product_card_style === "sharp" ? "2px" : "10px";

  const productsFor = (idx) => {
    if (products.length === 0) return [null, null, null];
    const start = (idx * 3) % products.length;
    return [0, 1, 2].map((i) => products[(start + i) % products.length]);
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-white/15 shadow-2xl" data-testid="tpl-live-preview" style={{ backgroundColor: bg, color: text, fontFamily: font }}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ backgroundColor: primary }}>
        {demoContent?.logo_url || d.logo_url ? (
          <img src={demoContent?.logo_url || d.logo_url} alt="logo" className="w-6 h-6 rounded object-cover" />
        ) : (
          <div className="w-6 h-6 rounded bg-white/30" />
        )}
        <span className="text-white text-sm font-black truncate" style={{ fontFamily: d.typography_heading || font }} data-testid="tpl-preview-brand">{brand}</span>
        <div className="ml-auto flex items-center gap-2">
          {d.header_style?.includes("search") && (
            <div className="bg-white/25 rounded-full px-3 py-1 text-[10px] text-white/90 w-28 truncate">Cari produk…</div>
          )}
          <div className="w-4 h-4 rounded bg-white/30" />
        </div>
      </div>

      {/* Banner — slider-aware: 0=fallback, 1=statis, 2-5=slider dots */}
      {banners.length === 0 ? (
        <div className="relative mx-3 mt-3 rounded-xl overflow-hidden grid place-items-center h-24"
          style={{ background: `linear-gradient(120deg, ${primary}22, ${accent}22)` }} data-testid="tpl-preview-banner-fallback">
          <div className="text-center px-4">
            <div className="font-black text-sm" style={{ fontFamily: d.typography_heading || font, color: text }}>{brand}</div>
            <div className="text-[9px] opacity-60">{d.business_description || "Belanja mudah, langsung dari toko kami"}</div>
          </div>
        </div>
      ) : (
        <div className="relative mx-3 mt-3 rounded-xl overflow-hidden" style={{ background: `linear-gradient(120deg, ${primary}, ${accent})` }} data-testid="tpl-preview-banner" data-slide-count={banners.length}>
          {banner?.image_url ? (
            <img src={banner.image_url} alt="" className="w-full h-24 object-cover transition-opacity duration-500" />
          ) : (
            <div className="h-24" />
          )}
          <div className={`absolute inset-0 flex flex-col justify-center px-4 ${banner?.text_alignment === "center" ? "items-center text-center" : banner?.text_alignment === "right" ? "items-end text-right" : ""}`}>
            {banner?.badge && <span className="text-[7px] uppercase font-bold text-white px-1.5 py-0.5 rounded-full mb-0.5 w-fit" style={{ backgroundColor: accent }}>{banner.badge}</span>}
            <div className="text-white font-black text-sm" style={{ fontFamily: d.typography_heading || font }}>{banner?.title || "Judul Banner"}</div>
            <div className="text-white/85 text-[10px]">{banner?.subtitle || "Subjudul promo Anda di sini"}</div>
          </div>
          {isSlider && (
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1" data-testid="tpl-preview-banner-dots">
              {banners.map((_, i) => (
                <span key={i} className="h-1.5 rounded-full transition-all duration-300" style={{ width: i === slide % banners.length ? 10 : 6, backgroundColor: i === slide % banners.length ? "#fff" : "rgba(255,255,255,0.4)" }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Inventory units chips */}
      {(dna?.inventory_units || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 mt-3" data-testid="tpl-preview-units">
          {(dna.inventory_units || []).map((u) => (
            <span key={u} className="text-[9px] font-bold px-2 py-0.5 rounded-full border" style={{ borderColor: primary, color: primary }}>{u}</span>
          ))}
        </div>
      )}

      {/* Homepage Sections */}
      <div className="px-3 py-3 space-y-4" data-testid="tpl-preview-sections">
        {(sections.length ? sections : ["Featured"]).slice(0, 5).map((sec, idx) => (
          <div key={`${sec}-${idx}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-black uppercase tracking-wide" style={{ fontFamily: d.typography_heading || font }}>{sec}</span>
              <span className="text-[9px] font-bold" style={{ color: primary }}>Lihat semua →</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {productsFor(idx).map((p, i) => (
                <div key={i} className="border overflow-hidden" style={{ borderColor: `${text}22`, borderRadius: cardRadius }}>
                  {p?.image_url ? (
                    <img src={p.image_url} alt="" className="w-full h-12 object-cover" />
                  ) : (
                    <div className="h-12" style={{ background: `linear-gradient(135deg, ${primary}33, ${accent}33)` }} />
                  )}
                  <div className="p-1.5">
                    <div className="text-[9px] font-bold truncate">{p?.name || "Nama Produk"}</div>
                    <div className="text-[9px] font-black" style={{ color: primary }}>
                      {p ? `Rp ${Number(p.price || 0).toLocaleString("id-ID")}` : "Rp 0"}
                      <span className="font-normal opacity-60"> /{p?.unit || (dna?.inventory_units || ["pcs"])[0]}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {sections.length === 0 && (
          <div className="text-[10px] opacity-50 text-center py-2">{d.empty_state_text || "Belum ada section."}</div>
        )}
        <button className="w-full py-1.5 text-[10px] font-black text-white" style={{ backgroundColor: primary, borderRadius: btnRadius }}>
          Belanja Sekarang
        </button>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 text-[9px]" style={{ backgroundColor: d.secondary_color || "#1e293b", color: "rgba(255,255,255,0.7)" }}>
        <div className="font-black text-white/90 mb-0.5">{brand}</div>
        <div className="line-clamp-2">{d.business_description || "Deskripsi bisnis Anda tampil di sini."}</div>
        <div className="mt-1 opacity-60">Footer · {d.footer_style || "columns-4"}</div>
      </div>
    </div>
  );
};

export default TemplateLivePreview;
